import { useEffect, useState } from "react";
import { isOtoInsightToDocsPair } from "../pages/AiPlanPage/ObjectToObjectPlan/otoDefaultPromptPair";
import { fetchOtoDefaultPrompt } from "../services/objectToObjectPlanService";

export type UseOtoDefaultPromptArgs = {
  referenceObjectType: string;
  impactedObjectType: string;
  /** When false, clears state and skips the request. */
  enabled?: boolean;
};

export type UseOtoDefaultPromptResult = {
  defaultPrompt: string;
  loading: boolean;
  error: string | null;
};

export function useOtoDefaultPrompt({
  referenceObjectType,
  impactedObjectType,
  enabled = true,
}: UseOtoDefaultPromptArgs): UseOtoDefaultPromptResult {
  const refType = referenceObjectType.trim();
  const impactType = impactedObjectType.trim();
  // Only Insight ↔ Docs has a server default prompt.
  const canFetch =
    enabled &&
    Boolean(refType) &&
    Boolean(impactType) &&
    isOtoInsightToDocsPair(refType, impactType);

  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canFetch) {
      setDefaultPrompt("");
      setLoading(false);
      setError(null);
      return;
    }

    let mounted = true;
    // Clear immediately so the field shows a loading overlay instead of a
    // blank or stale placeholder from the previous Reference/Impact pair.
    setLoading(true);
    setDefaultPrompt("");
    setError(null);

    fetchOtoDefaultPrompt({
      referenceObjectType: refType,
      impactedObjectType: impactType,
    })
      .then((prompt) => {
        if (!mounted) return;
        setDefaultPrompt(prompt);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setDefaultPrompt("");
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load default assessment prompt.",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [canFetch, impactType, refType]);

  return {
    defaultPrompt,
    loading,
    error,
  };
}
